// Gmail OAuth Service
import { OAUTH_CONFIG, generateOAuthState } from '../config/oauth';

export interface OAuthError {
  error: string;
  error_description?: string;
}

export interface GoogleUser {
  email: string;
  name: string;
  picture?: string;
  email_verified: boolean;
}

/**
 * Initiates Gmail OAuth flow
 * Opens Google consent screen in current window
 */
export const initiateGmailOAuth = (): void => {
  const { clientId, redirectUri, scopes, authUrl } = OAUTH_CONFIG.google;

  // Check if client ID is configured
  if (!clientId || clientId === '') {
    console.error('[OAuth] Missing VITE_GOOGLE_CLIENT_ID environment variable');
    alert('OAuth is not configured. Please add VITE_GOOGLE_CLIENT_ID to your .env file.');
    return;
  }

  // Generate and store state for CSRF protection
  const state = generateOAuthState();
  sessionStorage.setItem('oauth_state', state);

  // Build OAuth URL
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes.join(' '),
    state: state,
    access_type: 'offline', // Request refresh token
    prompt: 'consent' // Always show consent screen to get refresh token
  });

  const oauthUrl = `${authUrl}?${params.toString()}`;

  console.log('[OAuth] Initiating Gmail OAuth flow');
  console.log('[OAuth] Redirect URI:', redirectUri);

  // Redirect to Google OAuth
  window.location.href = oauthUrl;
};

/**
 * Validates OAuth state parameter (CSRF protection)
 */
export const validateOAuthState = (receivedState: string): boolean => {
  const storedState = sessionStorage.getItem('oauth_state');

  if (!storedState) {
    console.error('[OAuth] No stored state found');
    return false;
  }

  if (receivedState !== storedState) {
    console.error('[OAuth] State mismatch - possible CSRF attack');
    return false;
  }

  // Clear state after successful validation
  sessionStorage.removeItem('oauth_state');
  return true;
};

/**
 * Exchanges OAuth code for access token
 * Calls backend API endpoint
 */
export const exchangeCodeForToken = async (code: string): Promise<{
  token: string;
  user: GoogleUser;
}> => {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

  const response = await fetch(`${apiUrl}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error: OAuthError = await response.json();
    throw new Error(error.error_description || error.error || 'OAuth exchange failed');
  }

  return response.json();
};
